import { Component, Inject, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { filter, skip, Subscription } from 'rxjs';
import { Timestamp } from './timestamp/Timestamp';
import { EnvService } from '@shared/services/env.service';
import { LanguageService } from '@shared/services/language.service';
import { environment } from '../environments/environment';
import { ImportantNoticeComponent } from '@shared/components/important-notice/important-notice.component';
import { PrivacyNoticeComponent } from '@shared/components/privacy-notice/privacy-notice.component';
import { UmamiService } from '@shared/services/umami.service';
import { SeoService } from '@shared/services/seo.service';
import { AuthService } from '@shared/services/auth.service';
import { localizeUrl, normalizeContentLocale } from '@shared/utils/content-url';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    RouterModule,
    ImportantNoticeComponent,
    PrivacyNoticeComponent
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.css',
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'WlasnaSprawa';
  private navigationSub?: Subscription;
  private languageUrlSub?: Subscription;
  private userUrlSub?: Subscription;

  constructor(
    private router: Router,
    private envService: EnvService,
    private languageService: LanguageService,
    private authService: AuthService,
    private umamiService: UmamiService,
    private seoService: SeoService,
    @Inject(PLATFORM_ID) private platformId: object
  ) {
    // Log compiled timestamp
    console.log('compiled version timestamp:', Timestamp.stamp);
    
    // Log environment info
    console.log('Environment:', environment.envname);
    console.log('Production:', environment.production);
  }

  ngOnInit() {
    if (environment.envname !== 'production' && environment.envname !== 'lighthouse') {
      this.seoService.setNoIndex();
    }

    this.navigationSub = this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => {
        const path = event.urlAfterRedirects.split(/[?#]/, 1)[0];
        const publicContentRoute =
          /^\/(?:pl\/|uk\/)?public\/?$/.test(path) ||
          /^\/(?:pl\/|uk\/)?public\/(?:post|businessplan)\/[^/]+\/?$/.test(path) ||
          /^\/(?:pl\/|uk\/)?public\/(?:user\/[^/]+|view-analytics\/[^/]+)\/?$/.test(path) ||
          /^\/(?:pl\/|uk\/)?(?:signin|signup)\/?$/.test(path) ||
          /^\/(?:pl\/|uk\/)?(?:post|business-plan)\/\d+(?:-[^/]+)?\/?$/.test(path);
        if (!publicContentRoute) {
          this.seoService.setNoIndex();
        }
      });

    this.languageUrlSub = this.languageService.currentLanguage$
      .pipe(skip(1))
      .subscribe((locale) => this.syncRouteLocale(locale));
    this.userUrlSub = this.authService.currentUser
      .pipe(skip(1))
      .subscribe((user) => {
        if (user?.token) {
          this.syncRouteLocale(user.language_locale || 'en');
        }
      });

    // Initialize environment config loading
    this.envService.load();

    // Initialize translations and language selection
    this.languageService.initialize();

    // CHAT INTEGRATION POINT: a future ChatService should own the widget lifecycle;
    // keep provider SDK calls and account-data mapping out of this root component.
    // Chat is currently disabled. Self-hosted Chatwoot is the planned alternative,
    // but Tawk is not inherently prohibited; see the assessment in
    // documentation/FRONTEND_PRIVACY_AND_SUPPORT.md before choosing a provider.
    // Load chat on an explicit support action, with any required consent before
    // optional storage. Privacy-notice acknowledgment is not chat consent.
    // Start without account attributes; share only justified fields when needed.
    // Clear provider identity/session state on logout or account switch, and
    // dispose of language/auth subscriptions and widget listeners on teardown.
    // Configure the endpoint via EnvService after reviewing processor terms,
    // transfers, retention, and the behavior of the deployed widget.

    // Initialize analytics after environment config is resolved
    if (isPlatformBrowser(this.platformId)) {
      this.umamiService.initialize();
    }
    
    // Log environment config loading status
    this.envService.configurationPromise.then(() => {
      console.log('Environment config loaded:', this.envService.configuration);
      console.log('Config envname:', this.envService.config.envname ?? environment.envname);
    }).catch((err: Error) => {
      console.log('No config found in assets folder. Using environment variables.');
    });
  }

  ngOnDestroy(): void {
    this.navigationSub?.unsubscribe();
    this.languageUrlSub?.unsubscribe();
    this.userUrlSub?.unsubscribe();
  }

  private syncRouteLocale(locale: string): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const routerUrl = this.router.url;
    const browserUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    const currentUrl = routerUrl === '/' && browserUrl !== '/' ? browserUrl : routerUrl;
    const localizedUrl = localizeUrl(currentUrl, normalizeContentLocale(locale));
    if (localizedUrl !== currentUrl) {
      void this.router.navigateByUrl(localizedUrl, { replaceUrl: true });
    }
  }
}
